import { useDispatch, useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'
import Select from 'react-select'

import Page from '@/components/content/Page'
import Button from '@/components/form/Button'
import Input from '@/components/form/Input'
import { Translation } from '@/components/i18n/Translation'
import Error from '@/components/util/feedback/Error'
import LoaderCircle from '@/components/util/LoaderCircle'
import { getCustomerHeads } from '@/ducks/customersReducer'
import {
  getIsPostPending,
  getPostError,
  publishCustomer,
  getEditIsValid,
  getEditProp,
  setEditProp,
} from '@/ducks/newCustomerReducer'
import withCustomersLoaded from '@/hocs/withCustomersLoaded'
import { useIsBechtle } from '@/hooks/user'
import { appRoutes } from '@/util/misc'

import NewCustomerAddress from './NewCustomerAddress'
import NewCustomerContact from './NewCustomerContact'

import './new-customer.scss'

const NewCustomer = () => {
  const dispatch = useDispatch()
  const isBechtle = useIsBechtle()

  const name = useSelector(state => getEditProp(state, 'name'))
  const orgNbr = useSelector(state => getEditProp(state, 'orgNumber'))
  const vatNumber = useSelector(state => getEditProp(state, 'vatNumber'))
  const customerHeadId = useSelector(state => getEditProp(state, 'customerHeadId'))
  const customerId = useSelector(state => getEditProp(state, 'id'))
  const numberOfEmployees = useSelector(state => getEditProp(state, 'numberOfEmployees'))
  const customerHeads = useSelector(getCustomerHeads)
  const isFormValid = useSelector(state => getEditIsValid(state, isBechtle))
  const isPosting = useSelector(getIsPostPending)
  const error = useSelector(getPostError)
  // @ts-ignore
  const setValue = (prop, value) => dispatch(setEditProp({ prop, value }))

  const customerHeadOptions = [
    {
      value: 0,
      label: <Translation id="New enterprise" />,
    },
    // @ts-ignore
    ...customerHeads.map(ch => ({
      value: ch.id,
      label: ch.name,
    })),
  ]
  // @ts-ignore
  const setCustomerHeadId = selectedOption => {
    dispatch(setValue('customerHeadId', selectedOption.value))
  }

  if (customerId) {
    return <Navigate to={appRoutes.customer.replace(':customer_id', customerId)} />
  }

  return (
    <Page className="new-customer">
      <h1>
        <Translation id="New Customer" />
      </h1>

      <form>
        <div className="card essentials">
          <h2>
            <Translation id="Company" />
          </h2>

          <div className="name-and-org">
            <Input
              label={<Translation id="Customer Name" />}
              value={name || ''}
              onChange={e => setValue('name', e.target.value)}
              disabled={isPosting}
            />

            <Input
              label={<Translation id="Organization Number" />}
              value={orgNbr || ''}
              onChange={e => setValue('orgNumber', e.target.value)}
              disabled={isPosting}
              className="org-number"
              maxLength={20}
            />
          </div>

          {!isBechtle ? (
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor="entreprise-select">
                <Translation id="Choose enterprise" />
              </label>

              <Select
                id="entreprise-select"
                className="react-select"
                options={customerHeadOptions}
                onChange={setCustomerHeadId}
                value={customerHeadOptions.find(option => option.value === customerHeadId)}
              />
            </div>
          ) : (
            <div>
              <Input
                label={<Translation id="VAT Number" />}
                value={vatNumber || ''}
                onChange={e => setValue('vatNumber', e.target.value)}
                disabled={isPosting}
                className="vat-number"
                maxLength={20}
              />
            </div>
          )}

          {isBechtle && (
            <div>
              <Input
                label={<Translation id="Number of Employees" />}
                type="number"
                value={numberOfEmployees || ''}
                onChange={e => setValue('numberOfEmployees', e.target.value)}
                disabled={isPosting}
                className="number-of-employees"
                maxLength={20}
              />
            </div>
          )}
        </div>

        <NewCustomerAddress
          header={<Translation id={isBechtle ? 'Company address' : 'Street address'} />}
          // @ts-ignore
          dispatchAddress={address => setValue('streetAddress', address)}
          disabled={isPosting}
        />

        {!isBechtle ? (
          <NewCustomerAddress
            header={<Translation id="Billing address" />}
            // @ts-ignore
            dispatchAddress={address => setValue('billingAddress', address)}
            disabled={isPosting}
          />
        ) : (
          <NewCustomerContact
            header={<Translation id="Contact Person" />}
            // @ts-ignore
            dispatchContact={contactPerson => setValue('contactPerson', contactPerson)}
            disabled={isPosting}
          />
        )}

        {error && <Error textId={error} />}

        <div className="confirmation">
          {isPosting ? (
            <LoaderCircle />
          ) : (
            <Button
              type="button"
              disabled={!isFormValid}
              // @ts-ignore
              onClick={() => dispatch(publishCustomer())}
            >
              <Translation id="Create Customer" />
            </Button>
          )}
        </div>
      </form>
    </Page>
  )
}

export default withCustomersLoaded(NewCustomer)
